/**
 * Notification Sound Utility
 * Plays audio notifications for new reviews
 */

// Audio context for generating notification sounds
let audioContext = null;

// Get or create audio context (must be created after user interaction)
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Storage key for notification preferences
const NOTIFICATION_SOUND_KEY = 'elevate_notification_sound_enabled';

/**
 * Check if notification sounds are enabled
 */
export const isNotificationSoundEnabled = () => {
  const stored = localStorage.getItem(NOTIFICATION_SOUND_KEY);
  // Default to enabled if not set
  return stored === null ? true : stored === 'true';
};

/**
 * Set notification sound preference
 */
export const setNotificationSoundEnabled = (enabled) => {
  localStorage.setItem(NOTIFICATION_SOUND_KEY, enabled.toString());
};

/**
 * Play a pleasant notification chime
 * Uses Web Audio API to generate a two-tone chime
 */
export const playNotificationSound = () => {
  if (!isNotificationSoundEnabled()) {
    return;
  }

  try {
    const ctx = getAudioContext();

    // Resume audio context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;

    // Create a pleasant two-tone notification chime
    // First tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.frequency.value = 880; // A5
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0.3, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    osc1.start(now);
    osc1.stop(now + 0.3);

    // Second tone (slightly delayed)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1174.66; // D6
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0, now + 0.1);
    gain2.gain.linearRampToValueAtTime(0.3, now + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.5);

  } catch (error) {
    console.warn('Could not play notification sound:', error);
  }
};

/**
 * Request notification permission and initialize audio context
 * Call this on first user interaction (e.g., button click)
 */
export const initializeNotifications = async () => {
  // Initialize audio context on user interaction
  getAudioContext();

  // Request browser notification permission (for future use)
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      await Notification.requestPermission();
    } catch (error) {
      console.warn('Could not request notification permission:', error);
    }
  }
};
